
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { InvoiceGroup, Client } from "@/types/invoice";
import InvoiceTable from "./InvoiceTable";
import { useTranslation } from "@/i18n/useTranslation";
import { Language } from "@/i18n/translations";

interface InvoiceListProps {
  groupedInvoices: InvoiceGroup[];
  clients: Client[];
  defaultOpenValue?: string;
  onNewInvoice: (periodKey: string) => void;
  language: Language;
}

const InvoiceList = ({ 
  groupedInvoices, 
  clients, 
  defaultOpenValue,
  onNewInvoice,
  language
}: InvoiceListProps) => {
  const { t } = useTranslation();

  return (
    <Accordion type="single" collapsible className="w-full" defaultValue={defaultOpenValue}>
      {groupedInvoices.map((group) => (
        <AccordionItem key={group.periodKey} value={group.periodKey}>
          <AccordionTrigger className="hover:bg-muted/50 px-4">
            <div className="flex items-center gap-2">
              <span className="font-medium">{group.period}</span>
              {group.isCurrent && (
                <Badge variant="default" className="bg-green-500 hover:bg-green-600 text-white">
                  {t('invoices.current')}
                </Badge>
              )}
              <span className="ml-2 text-muted-foreground">
                ({group.invoices.length} {group.invoices.length === 1 ? "invoice" : "invoices"})
              </span>
            </div>
            <div className="flex-1"></div>
            <Button 
              size="icon"
              variant="outline"
              className="ml-auto"
              onClick={(e) => {
                e.stopPropagation();
                onNewInvoice(group.periodKey);
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </AccordionTrigger>
          <AccordionContent>
            <InvoiceTable 
              invoices={group.invoices} 
              clients={clients}
              language={language}
            />
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export default InvoiceList;
